import React, { useState, useRef } from "react"
import { Link } from "gatsby"
import styled from "styled-components"
import useIntersectionObserver from "src/hooks/useIntersectionObserver"
import { media } from "src/styles/breakpoints"
import { TertiaryTitle } from "src/components/Typography"
import BaseIcon from "src/components/Base/BaseIcon"

const BoxList = ({ items }) => {
  const [observed, setObserved] = useState(false)
  const wrapper = useRef()

  useIntersectionObserver({
    callback: (change) => {
      if (change.isIntersecting) {
        setObserved(true)
      } else {
        // setObserved(false)
      }
    },
    elementRef: wrapper,
    options: {
      rootMargin: "-100px",
    },
  })

  return (
    <Wrapper ref={wrapper} className={observed ? "-observed" : ""}>
      {items.map(({ title, subtitle, path, icon }, index) => (
        <Box to={path} key={index}>
          <Outline />
          <IconWrapper>
            <Icon name={icon} />
          </IconWrapper>
          <Title>{title}</Title>
          <Description>{subtitle}</Description>
        </Box>
      ))}
    </Wrapper>
  )
}

const boxMargin = 30

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 130px;

  &::after {
    content: "";
    width: calc((100% - ${boxMargin}px * 2) / 3);
  }

  ${media.tablet} {
    margin-bottom: 190px;
  }
`

const Box = styled(Link)`
  width: 100%;
  margin-bottom: ${boxMargin}px;
  padding: 25px 25px 35px;
  position: relative;
  border: 1px solid #cfdbdb;
  border-radius: 3px;
  text-align: center;
  transition: 0.6s ${(p) => p.theme.easing.easeOutQuart};

  > * {
    transition: 0.6s ${(p) => p.theme.easing.easeOutQuart};

    div${Wrapper}:not(.-observed) & {
      opacity: 0;
      transform: translateY(20px);
    }
  }

  &::before {
    content: "";
    opacity: 0;
    position: absolute;
    top: -1px;
    right: -1px;
    bottom: -1px;
    left: -1px;
    box-shadow: 0 40px 60px rgba(0, 11, 33, 0.06);
    transition: opacity 0.15s;
  }

  &:nth-child(1) {
    transition-delay: 0.15s;

    > :nth-child(2) {
      transition-delay: 0.2s;
    }

    > :nth-child(3) {
      transition-delay: 0.25s;
    }

    > :nth-child(4) {
      transition-delay: 0.3s;
    }
  }

  &:nth-child(2) {
    transition-delay: 0.3s;

    > :nth-child(2) {
      transition-delay: 0.35s;
    }

    > :nth-child(3) {
      transition-delay: 0.4s;
    }

    > :nth-child(4) {
      transition-delay: 0.45s;
    }
  }

  &:nth-child(3) {
    transition-delay: 0.45s;

    > :nth-child(2) {
      transition-delay: 0.5s;
    }

    > :nth-child(3) {
      transition-delay: 0.55s;
    }

    > :nth-child(4) {
      transition-delay: 0.6s;
    }
  }

  &:nth-child(4) {
    transition-delay: 0.6s;

    > :nth-child(2) {
      transition-delay: 0.65s;
    }

    > :nth-child(3) {
      transition-delay: 0.7s;
    }

    > :nth-child(4) {
      transition-delay: 0.75s;
    }
  }

  &:nth-child(5) {
    transition-delay: 0.75s;

    > :nth-child(2) {
      transition-delay: 0.8s;
    }

    > :nth-child(3) {
      transition-delay: 0.85s;
    }

    > :nth-child(4) {
      transition-delay: 0.9s;
    }
  }

  &:nth-child(6) {
    transition-delay: 0.9s;

    > :nth-child(2) {
      transition-delay: 0.95s;
    }

    > :nth-child(3) {
      transition-delay: 1s;
    }

    > :nth-child(4) {
      transition-delay: 1.05s;
    }
  }

  &:hover {
    &::before {
      opacity: 1;
    }
  }

  div${Wrapper}:not(.-observed) & {
    opacity: 0;
    transform: translateY(20px);
  }

  ${media.tablet} {
    width: calc((100% - ${boxMargin}px * 1) / 2);
    padding: 60px 25px 68px;
  }

  ${media.desktop} {
    width: calc((100% - ${boxMargin}px * 2) / 3);
  }
`

const IconWrapper = styled.span`
  display: block;
  margin-bottom: 20px;

  ${media.tablet} {
    margin-bottom: 75px;
  }
`

const Icon = styled(BaseIcon)`
  width: 24px;
  height: 24px;
  position: relative;
  color: #4D5B5B;
  transition: color 0.15s;

  &[data-name="leaf"] {
    width: 23px;
    height: 23px;
  }

  ${Box}:hover & {
    color: #46cad8;
  }
`

const Title = styled(TertiaryTitle)`
  margin-bottom: 18px;
  position: relative;
`

const Description = styled.p`
  /* max-width: 220px; */
  margin-left: auto;
  margin-right: auto;
  position: relative;
  font-size: 15px;
  color: ${(p) => p.theme.color.textLight};
`

const Outline = styled.div`
  opacity: 0;
  position: absolute;
  top: -1px;
  right: -1px;
  bottom: -1px;
  left: -1px;
  background: linear-gradient(-20deg, #3ab6da, #50d9d6);
  border-radius: 3px;
  transition: opacity 0.15s;

  &::before {
    content: "";
    position: absolute;
    top: 3px;
    right: 3px;
    bottom: 3px;
    left: 3px;
    background: #fff;
  }

  ${Box}:hover & {
    opacity: 1;
  }
`

export default BoxList
